import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const MOIRE_URL = "https://moire.live";
const KC_CINEM_003_PATH = "/collections/kc-cinem/003-1-the-techcare-reaper";

const trackMoire = (section, cta) => {
  if (typeof window !== "undefined" && typeof window.trackMoire === "function") {
    window.trackMoire(section, cta);
  }
};

export default function MoireSection() {
  return (
    <section
      id="moire"
      data-testid="moire-section"
      className="relative py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-[#0B0B0B] border-t border-b border-[#1a1a1a] overflow-hidden"
    >
      {/* Subtle champagne wash */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 75% 30%, #D4AF37 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left — editorial copy */}
        <div className="lg:col-span-6 reveal">
          <span
            data-testid="moire-eyebrow"
            className="text-[10px] tracking-[0.42em] uppercase text-[#D4AF37]"
          >
            Kendi Collections Presents
          </span>

          <h2
            data-testid="moire-heading"
            className="mt-6 font-serif-display text-[42px] sm:text-5xl md:text-6xl lg:text-[68px] leading-[0.98] tracking-tight text-white"
          >
            Introducing{" "}
            <span className="italic text-gold-shimmer">MOIRÉ.</span>
          </h2>

          <p className="mt-6 text-[15px] md:text-base leading-[1.6] text-white/80 font-light">
            Your personal styling intelligence.
          </p>

          <div className="mt-8 space-y-5 max-w-xl">
            <p className="text-[14px] md:text-[15px] leading-[1.9] text-white/60 font-light">
              Not sure what works with what you already own? MOIRÉ helps you
              discover what matches, complete your look and even rethink your
              space — using intelligent styling built around what you already
              have.
            </p>
            <p className="text-[12px] tracking-[0.32em] uppercase text-white/50">
              MOIRÉ helps you style what you wear <span className="text-[#D4AF37]/70">◆</span> and where you live
            </p>
          </div>

          <div className="mt-10">
            <span
              data-testid="moire-tagline"
              className="block font-serif-display italic text-2xl md:text-3xl text-white/85 mb-8"
            >
              Dress up. Don&apos;t mess up.
            </span>

            <a
              href={MOIRE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackMoire("homepage_moire_section", "discover_cta")}
              data-testid="moire-discover-btn"
              className="btn-outline-gold"
            >
              <span>Discover MOIRÉ</span>
              <ArrowUpRight className="w-4 h-4" />
            </a>

            <p className="mt-6 text-[10px] tracking-[0.32em] uppercase text-white/40">
              MOIRÉ — Powered by Kendi Collections
            </p>

            <p className="mt-8 pt-6 border-t border-[#1a1a1a] text-[13px] text-white/55 font-light">
              Designed by Kendi Collections for MOIRÉ:{" "}
              <Link
                to={KC_CINEM_003_PATH}
                onClick={() =>
                  typeof window !== "undefined" &&
                  window.trackNavigation &&
                  window.trackNavigation("KC-CINEM 003.1", KC_CINEM_003_PATH, false)
                }
                data-testid="moire-kc-cinem-003-link"
                className="text-[#D4AF37] hover:text-white transition-colors inline-flex items-center gap-1.5"
              >
                KC-CINEM 003.1 — The Techcare Reaper
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </p>
          </div>
        </div>

        {/* Right — type-first "logo" plate (until real asset is provided) */}
        <div className="lg:col-span-6 reveal">
          <a
            href={MOIRE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackMoire("homepage_moire_section", "wordmark_plate")}
            aria-label="Discover MOIRÉ"
            data-testid="moire-plate-link"
            className="block relative aspect-[5/6] md:aspect-[4/5] border border-[#D4AF37]/25 hover:border-[#D4AF37]/60 transition-all duration-700 group overflow-hidden"
          >
            {/* Corner ornaments */}
            <span className="absolute top-0 left-0 w-px h-6 bg-[#D4AF37]/70" />
            <span className="absolute top-0 left-0 h-px w-6 bg-[#D4AF37]/70" />
            <span className="absolute top-0 right-0 w-px h-6 bg-[#D4AF37]/70" />
            <span className="absolute top-0 right-0 h-px w-6 bg-[#D4AF37]/70" />
            <span className="absolute bottom-0 left-0 w-px h-6 bg-[#D4AF37]/70" />
            <span className="absolute bottom-0 left-0 h-px w-6 bg-[#D4AF37]/70" />
            <span className="absolute bottom-0 right-0 w-px h-6 bg-[#D4AF37]/70" />
            <span className="absolute bottom-0 right-0 h-px w-6 bg-[#D4AF37]/70" />

            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 md:px-10 text-center">
              <span className="text-[9px] tracking-[0.42em] uppercase text-[#D4AF37]/80">
                A new experience
              </span>
              <span
                className="mt-6 md:mt-8 font-serif-display italic text-gold-shimmer leading-[0.9] tracking-tight text-[64px] sm:text-[92px] md:text-[120px] lg:text-[140px] group-hover:tracking-wide transition-all duration-700"
                style={{ letterSpacing: "-0.02em" }}
              >
                Moiré
              </span>
              <span className="mt-6 md:mt-8 text-[10px] tracking-[0.42em] uppercase text-white/45">
                Style · Home
              </span>
            </div>

            {/* Fine grain */}
            <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
          </a>
        </div>
      </div>
    </section>
  );
}
